'use client'

import { useEffect } from 'react'

/**
 * Fire-and-forget content-view beacon (POST /api/content-views). Fires at most
 * once per content per browser session (sessionStorage), skips speculative
 * prerenders and automation, and prefers navigator.sendBeacon so it survives the
 * page unloading. Renders nothing.
 *
 * The server derives platform (web/mobile) from the auth transport and dedups
 * per (user, content, UTC day), and only persists for logged-in users — so this
 * is a best-effort nudge, not an exact counter. `contentId` may be a
 * content_index.id (uuid) OR a Sanity _id; the route resolves either.
 *
 * Note: <Link> prefetch only fetches the RSC payload server-side; it does NOT
 * mount client components or run effects, so it never fires the beacon. Only a
 * full prerender executes JS, which the prerendering guard below defers.
 */
export function RecordView({ contentId }: { contentId: string | null | undefined }) {
  useEffect(() => {
    if (!contentId) return
    // Skip headless crawlers / Lighthouse.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return

    const key = `theduchy.content-view.${contentId}`

    const fire = () => {
      try {
        if (sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')
      } catch {
        /* private mode: fall through — the server's per-day dedup still bounds it */
      }
      const payload = JSON.stringify({ content_id: contentId })
      try {
        const blob = new Blob([payload], { type: 'application/json' })
        if (navigator.sendBeacon?.('/api/content-views', blob)) return
      } catch {
        /* fall back to fetch */
      }
      fetch('/api/content-views', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {})
    }

    // Chrome Speculation-Rules prerender runs JS before the user commits; defer
    // until the page is actually activated so prerenders aren't counted.
    const doc = document as Document & { prerendering?: boolean }
    if (typeof document !== 'undefined' && doc.prerendering) {
      const onActivate = () => fire()
      document.addEventListener('prerenderingchange', onActivate, { once: true })
      return () => document.removeEventListener('prerenderingchange', onActivate)
    }
    fire()
  }, [contentId])

  return null
}
